/**
 * OrgConversationsPage Redux duck.
 *
 * Thin wrapper over the shared conversations factory (see
 * src/ducks/conversations.shared.js). This file only adds
 * organization-specific bits:
 *  - org team roster slice + thunk (used by the DM picker)
 *  - updateChannelVisibility thunk (public/private toggle)
 *  - fetchSendbirdToken (uses org profile image)
 *  - selectChannelsByType (org-side grouping: shows / team / dms)
 *  - selectAllUniqueMembers (team roster + channel members, Slack-style)
 *  - loadData (kicks off team-roster fetch in parallel with token)
 */

import { CHANNEL_TYPES } from '../../util/sendbird';
import { sendbirdAPI } from '../../util/api';
import { orgTeamAPI } from '../../api';
import { decodeChannelId } from '../../util/channelUrl';
import { createConversationsDuck } from '../../ducks/conversations.shared';

const NAMESPACE = 'OrgConversationsPage';
const duck = createConversationsDuck(NAMESPACE);

// ============== Re-export shared action types ============== //
export const {
  SET_SENDBIRD_CONNECTED,
  FETCH_TOKEN_REQUEST,
  FETCH_TOKEN_SUCCESS,
  FETCH_TOKEN_ERROR,
  SET_CHANNELS,
  ADD_CHANNELS,
  ADD_CHANNEL,
  UPDATE_CHANNEL,
  UPDATE_CHANNELS,
  REMOVE_CHANNEL,
  REMOVE_CHANNELS,
  SET_HAS_MORE_CHANNELS,
  SET_SELECTED_CHANNEL,
  SET_MESSAGES,
  ADD_MESSAGE,
  ADD_MESSAGES,
  PREPEND_MESSAGES,
  UPDATE_MESSAGE,
  UPDATE_MESSAGES,
  REMOVE_MESSAGE,
  REMOVE_MESSAGES,
  SET_HAS_MORE_MESSAGES,
  SET_LOADING_MORE_MESSAGES,
  SET_THREAD_MESSAGES,
  ADD_THREAD_MESSAGE,
  SET_ACTIVE_THREAD,
  SET_THREAD_LOADING,
  SET_ARCHIVE_IN_PROGRESS,
  SET_UNARCHIVE_IN_PROGRESS,
  SET_DELETE_IN_PROGRESS,
  SET_CHANNEL_INFO_OPEN,
  SET_ACTIVE_TAB,
  SET_CHANNEL_MEMBERS,
  SET_CHANNEL_BOOKINGS,
  SET_PENDING_CHANNEL_URL,
  FETCH_CHANNELS_REQUEST,
  FETCH_CHANNELS_SUCCESS,
  FETCH_CHANNELS_ERROR,
  FETCH_MESSAGES_REQUEST,
  FETCH_MESSAGES_SUCCESS,
  FETCH_MESSAGES_ERROR,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_ERROR,
  CREATE_CHANNEL_REQUEST,
  CREATE_CHANNEL_SUCCESS,
  CREATE_CHANNEL_ERROR,
} = duck.types;

// Org-only action types
export const FETCH_ORG_TEAM_REQUEST = `app/${NAMESPACE}/FETCH_ORG_TEAM_REQUEST`;
export const FETCH_ORG_TEAM_SUCCESS = `app/${NAMESPACE}/FETCH_ORG_TEAM_SUCCESS`;
export const FETCH_ORG_TEAM_ERROR = `app/${NAMESPACE}/FETCH_ORG_TEAM_ERROR`;

// ============== Reducer (wraps shared reducer + org-team extension) ============== //

const orgTeamInitial = {
  orgTeamMembers: [],
  orgTeamLoading: false,
  orgTeamError: null,
};

const baseReducer = duck.reducer;

export default function reducer(state, action) {
  // Seed org-team slice on first call so it survives the very first
  // dispatch — same pattern as the shared reducer's `state = initialState`.
  const seeded =
    state === undefined
      ? { ...baseReducer(undefined, action), ...orgTeamInitial }
      : state;

  switch (action.type) {
    case FETCH_ORG_TEAM_REQUEST:
      return { ...seeded, orgTeamLoading: true, orgTeamError: null };
    case FETCH_ORG_TEAM_SUCCESS:
      return { ...seeded, orgTeamLoading: false, orgTeamMembers: action.payload };
    case FETCH_ORG_TEAM_ERROR:
      return { ...seeded, orgTeamLoading: false, orgTeamError: action.payload };
    default:
      return baseReducer(seeded, action);
  }
}

// ============== Re-export shared action creators ============== //
export const {
  setSendbirdConnected,
  setChannels,
  addChannels,
  addChannel,
  updateChannel,
  updateChannels,
  removeChannel,
  removeChannels,
  setHasMoreChannels,
  setSelectedChannel,
  setMessages,
  addMessage,
  addMessages,
  prependMessages,
  updateMessage,
  updateMessages,
  removeMessage,
  removeMessages,
  setHasMoreMessages,
  setLoadingMoreMessages,
  setThreadMessages,
  addThreadMessage,
  setActiveThread,
  setThreadLoading,
  setArchiveInProgress,
  setUnarchiveInProgress,
  setDeleteInProgress,
  setChannelInfoOpen,
  setActiveTab,
  setChannelMembers,
  setChannelBookings,
  setPendingChannelUrl,
} = duck.actions;

// ============== Re-export shared thunks ============== //
export const {
  fetchChannelByUrl,
  fetchChannels,
  createChannel,
  archiveChannel,
  unarchiveChannel,
  deleteChannel,
  leaveChannel,
  addChannelMembers,
  removeChannelMember,
} = duck.thunks;

// ============== Re-export shared selectors ============== //
export const {
  selectSendbirdConnected,
  selectTokenLoading,
  selectTokenError,
  selectSendbirdToken,
  selectChannels,
  selectHasMoreChannels,
  selectSelectedChannel,
  selectMessages,
  selectMessagesLoading,
  selectHasMoreMessages,
  selectLoadingMoreMessages,
  selectActiveTab,
  selectChannelInfoOpen,
  selectActiveThreadMessageId,
  selectThreadMessages,
  selectThreadLoading,
  selectArchiveInProgress,
  selectUnarchiveInProgress,
  selectDeleteInProgress,
  selectChannelMembers,
  selectChannelBookings,
  selectPendingChannelUrl,
  selectTotalUnreadCount,
} = duck.selectors;

// ============== Org-specific thunks ============== //

/**
 * Fetch Sendbird session token. Uses the selected org's profile image
 * as the Sendbird avatar (so admin/system messages posted on behalf of
 * the org show the org's picture).
 */
export const fetchSendbirdToken = orgId => async (dispatch, getState) => {
  dispatch({ type: FETCH_TOKEN_REQUEST });
  try {
    const { organizationProfiles } = getState().organizations || {};
    const selectedOrg = organizationProfiles?.find(org => org.id === orgId);
    const orgProfileImage =
      selectedOrg?.attributes?.publicData?.profileImage?.link;

    const response = await sendbirdAPI.getToken({
      profileUrl: orgProfileImage || undefined,
    });
    dispatch({ type: FETCH_TOKEN_SUCCESS, payload: response });
    return response;
  } catch (error) {
    console.error('Fetch token error:', error);
    dispatch({ type: FETCH_TOKEN_ERROR, payload: error.message });
    throw error;
  }
};

export const updateChannelVisibility = (channelUrl, isPrivate) => async dispatch => {
  try {
    const updatedChannel = await sendbirdAPI.updateChannel(channelUrl, {
      isPrivate,
    });
    dispatch(updateChannel(updatedChannel));
    return updatedChannel;
  } catch (error) {
    console.error('Update channel visibility error:', error);
    throw error;
  }
};

/**
 * Fetch the org's active team roster so the DM picker can show every
 * teammate as a possible recipient (Slack-style). Pending invites are
 * dropped — they have no userId yet and can't receive Sendbird messages.
 */
export const fetchOrgTeam = orgId => async dispatch => {
  if (!orgId) return [];
  dispatch({ type: FETCH_ORG_TEAM_REQUEST });
  try {
    const response = await orgTeamAPI.list(orgId);
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    const teamMembers = (data?.teamMembers || []).filter(
      m => m?.userId && (m.status ? m.status === 'active' : true)
    );
    dispatch({ type: FETCH_ORG_TEAM_SUCCESS, payload: teamMembers });
    return teamMembers;
  } catch (e) {
    dispatch({ type: FETCH_ORG_TEAM_ERROR, payload: e?.message || 'fetch failed' });
    return [];
  }
};

// ============== Org-specific selectors ============== //

export const selectChannelsByType = state => {
  const channels = selectChannels(state) || [];
  const grouped = { shows: [], team: [], dms: [] };
  channels.forEach(channel => {
    switch (channel.customType) {
      case CHANNEL_TYPES.SHOW:
        grouped.shows.push(channel);
        break;
      case CHANNEL_TYPES.TEAM:
        grouped.team.push(channel);
        break;
      case CHANNEL_TYPES.DM:
        grouped.dms.push(channel);
        break;
      default:
        grouped.team.push(channel);
    }
  });
  return grouped;
};

/**
 * DM-eligible recipient pool for an org viewer: every active teammate
 * plus anyone else already in a channel with the viewer (covers
 * historical conversations / non-team interactions). Pending invites
 * are excluded; current user is filtered out. Returned in react-select
 * shape, alphabetised by label.
 */
export const selectAllUniqueMembers = (state, currentUserId) => {
  const channels = selectChannels(state) || [];
  const orgTeam = state[NAMESPACE]?.orgTeamMembers || [];
  const membersMap = new Map();

  // Team roster first — these are the canonical recipients.
  orgTeam.forEach(m => {
    if (!m?.userId || m.userId === currentUserId) return;
    if (m.status && m.status !== 'active') return;
    if (membersMap.has(m.userId)) return;
    membersMap.set(m.userId, {
      value: m.userId,
      label: m.displayName || m.email || m.userId,
      avatar: m.avatarUrl || null,
      userId: m.userId,
      role: m.role || null,
    });
  });

  // Fold in past channel members so they stay reachable even if they're
  // no longer on the team roster.
  channels.forEach(channel => {
    (channel.members || []).forEach(member => {
      if (member.userId === currentUserId) return;
      if (membersMap.has(member.userId)) return;
      membersMap.set(member.userId, {
        value: member.userId,
        label: member.nickname || member.userId,
        avatar: member.profileUrl || null,
        ...member,
      });
    });
  });

  return Array.from(membersMap.values()).sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase())
  );
};

// ============== loadData ============== //

export const loadData = params => async dispatch => {
  const { channelId, orgId } = params;
  if (channelId) {
    dispatch(setPendingChannelUrl(decodeChannelId(channelId)));
  } else {
    dispatch(setPendingChannelUrl(null));
  }
  // Don't await — the page can render without the team data.
  if (orgId) dispatch(fetchOrgTeam(orgId));
  return dispatch(fetchSendbirdToken(orgId));
};
